export interface ResourceAction {
  key: string;
  label: string;
  allowedStatuses?: string[];
  danger?: boolean;
}

export interface ModuleResource {
  key: string;
  label: string;
  endpoint: string;
  description: string;
  actions?: ResourceAction[];
}

export interface ErpModule {
  code: string;
  label: string;
  description: string;
  resources: ModuleResource[];
}

const DOCUMENT_ACTIONS: ResourceAction[] = [
  { key: 'submit', label: 'Gửi duyệt', allowedStatuses: ['DRAFT'] },
  { key: 'finalize', label: 'Đồng bộ duyệt', allowedStatuses: ['PENDING_APPROVAL'] },
  { key: 'post', label: 'Ghi sổ', allowedStatuses: ['APPROVED'] },
];

const ACTIVATION_ACTIONS: ResourceAction[] = [
  { key: 'activate', label: 'Kích hoạt', allowedStatuses: ['INACTIVE'] },
  { key: 'deactivate', label: 'Ngừng sử dụng', allowedStatuses: ['ACTIVE'], danger: true },
];

export const ERP_MODULES: ErpModule[] = [
  {
    code: 'sys',
    label: 'Hệ thống',
    description: 'Workflow, thông báo, nhật ký kiểm toán, cấu hình và số chứng từ.',
    resources: [
      { key: 'inbox', label: 'Việc cần duyệt', endpoint: '/workflow/inbox', description: 'Hộp thư phê duyệt hợp nhất.' },
      { key: 'notifications', label: 'Thông báo', endpoint: '/notifications', description: 'Thông báo nghiệp vụ của bạn.' },
      { key: 'settings', label: 'Cấu hình', endpoint: '/sys/settings', description: 'Tham số nghiệp vụ theo công ty.' },
      { key: 'sequences', label: 'Số chứng từ', endpoint: '/sys/doc-sequences', description: 'Quy tắc đánh số chứng từ.' },
      { key: 'audit', label: 'Nhật ký kiểm toán', endpoint: '/sys/audit-logs', description: 'Lịch sử thay đổi bất biến.' },
    ],
  },
  {
    code: 'iam',
    label: 'Định danh & phân quyền',
    description: 'Người dùng, vai trò, SoD, ủy quyền và phiên đăng nhập.',
    resources: [
      { key: 'users', label: 'Người dùng', endpoint: '/iam/users', description: 'Tài khoản đồng bộ từ SSO.' },
      { key: 'roles', label: 'Vai trò', endpoint: '/iam/roles', description: 'Vai trò và tập quyền.' },
      { key: 'delegations', label: 'Ủy quyền', endpoint: '/iam/delegations/mine', description: 'Ủy quyền phê duyệt đang hiệu lực.' },
      { key: 'sod', label: 'Vi phạm SoD', endpoint: '/iam/sod-violations', description: 'Xung đột quyền cần xử lý.' },
      { key: 'sessions', label: 'Phiên của tôi', endpoint: '/iam/me/sessions', description: 'Các phiên đăng nhập đang hoạt động.' },
    ],
  },
  {
    code: 'mdm',
    label: 'Danh mục dùng chung',
    description: 'Công ty, tổ chức, tài khoản, tiền tệ, đối tác và kỳ kế toán.',
    resources: [
      { key: 'companies', label: 'Công ty', endpoint: '/mdm/companies', description: 'Cơ cấu pháp nhân.', actions: ACTIVATION_ACTIONS },
      { key: 'accounts', label: 'Hệ thống tài khoản', endpoint: '/mdm/accounts', description: 'COA công ty và ánh xạ tập đoàn.', actions: ACTIVATION_ACTIONS },
      { key: 'partners', label: 'Đối tác', endpoint: '/mdm/partners', description: 'Khách hàng và nhà cung cấp.' },
      { key: 'departments', label: 'Phòng ban', endpoint: '/mdm/departments', description: 'Cây tổ chức và phòng ban.' },
      { key: 'cost-centers', label: 'Trung tâm chi phí', endpoint: '/mdm/cost-centers', description: 'Chiều phân tích chi phí.' },
      { key: 'tax-codes', label: 'Mã thuế', endpoint: '/mdm/tax-codes', description: 'Thuế suất và tài khoản thuế.', actions: ACTIVATION_ACTIONS },
      { key: 'exchange-rates', label: 'Tỷ giá', endpoint: '/mdm/exchange-rates', description: 'Tỷ giá theo ngày và nguồn.' },
      { key: 'fiscal-years', label: 'Năm tài chính', endpoint: '/mdm/fiscal-years', description: 'Kỳ kế toán theo module.' },
      { key: 'changes', label: 'Yêu cầu thay đổi', endpoint: '/mdm/change-requests', description: 'Maker–checker cho danh mục trọng yếu.' },
    ],
  },
  {
    code: 'gl',
    label: 'Sổ cái',
    description: 'Bút toán, mẫu định kỳ, phân bổ, đánh giá lại và khóa sổ.',
    resources: [
      { key: 'journals', label: 'Bút toán', endpoint: '/gl/journal-entries', description: 'Vòng đời bút toán và ghi sổ.', actions: DOCUMENT_ACTIONS },
      { key: 'templates', label: 'Mẫu bút toán', endpoint: '/gl/templates', description: 'Mẫu và lịch sinh định kỳ.', actions: ACTIVATION_ACTIONS },
      { key: 'allocations', label: 'Quy tắc phân bổ', endpoint: '/gl/allocation-rules', description: 'Phân bổ chi phí cuối kỳ.', actions: ACTIVATION_ACTIONS },
      { key: 'consolidation', label: 'Hợp nhất', endpoint: '/gl/consolidation/groups', description: 'Nhóm hợp nhất và loại trừ nội bộ.' },
    ],
  },
  {
    code: 'ap',
    label: 'Công nợ phải trả',
    description: 'Hóa đơn nhà cung cấp, thanh toán, trả trước và đối chiếu.',
    resources: [
      { key: 'invoices', label: 'Hóa đơn nhà cung cấp', endpoint: '/ap/invoices', description: 'Nhập, đối chiếu 3 chiều và duyệt hóa đơn.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
      { key: 'payments', label: 'Đề nghị thanh toán', endpoint: '/ap/payment-requests', description: 'Gom hóa đơn và xuất lệnh thanh toán.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
      { key: 'prepayments', label: 'Trả trước', endpoint: '/ap/prepayments', description: 'Khoản trả trước và đối trừ.' },
      { key: 'statements', label: 'Đối chiếu NCC', endpoint: '/ap/vendor-statements', description: 'Biên bản đối chiếu công nợ.' },
    ],
  },
  {
    code: 'ar',
    label: 'Công nợ phải thu',
    description: 'Hóa đơn khách hàng, thu tiền, nhắc nợ và dự phòng.',
    resources: [
      { key: 'invoices', label: 'Hóa đơn khách hàng', endpoint: '/ar/invoices', description: 'Phát hành và theo dõi hóa đơn.' },
      { key: 'receipts', label: 'Phiếu thu', endpoint: '/ar/receipts', description: 'Thu tiền và khớp hóa đơn.', actions: [{ key: 'post', label: 'Ghi sổ', allowedStatuses: ['DRAFT'] }] },
      { key: 'recurring', label: 'Hóa đơn định kỳ', endpoint: '/ar/recurring-templates', description: 'Mẫu hóa đơn dịch vụ định kỳ.', actions: ACTIVATION_ACTIONS },
      { key: 'provisions', label: 'Dự phòng nợ', endpoint: '/ar/provision-runs', description: 'Tính dự phòng theo tuổi nợ.' },
    ],
  },
  {
    code: 'tr',
    label: 'Ngân quỹ',
    description: 'Tài khoản ngân hàng, sổ phụ, đối soát, dòng tiền và khoản vay.',
    resources: [
      { key: 'accounts', label: 'Tài khoản ngân hàng', endpoint: '/tr/bank-accounts', description: 'Tài khoản và hạn mức ký duyệt.', actions: ACTIVATION_ACTIONS },
      { key: 'statements', label: 'Sổ phụ & đối soát', endpoint: '/tr/bank-statements', description: 'Import và đối soát giao dịch ngân hàng.' },
      { key: 'cash', label: 'Phiếu thu chi', endpoint: '/tr/cash-vouchers', description: 'Quỹ tiền mặt theo đơn vị.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
      { key: 'transfers', label: 'Chuyển tiền nội bộ', endpoint: '/tr/internal-transfers', description: 'Chuyển tiền và bút toán hai đầu.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
      { key: 'loans', label: 'Tiền gửi & khoản vay', endpoint: '/tr/deposit-loans', description: 'Hợp đồng, lãi dồn tích và đáo hạn.' },
      { key: 'releases', label: 'Thực hiện chi', endpoint: '/tr/payment-releases', description: 'Release thanh toán theo nguyên tắc bốn mắt.' },
    ],
  },
  {
    code: 'fa',
    label: 'Tài sản cố định',
    description: 'Tài sản VAS/IFRS, khấu hao, điều chuyển, thanh lý và kiểm kê.',
    resources: [
      { key: 'assets', label: 'Thẻ tài sản', endpoint: '/fa/assets', description: 'Thẻ tài sản và hai sổ VAS/IFRS.' },
      { key: 'depreciation', label: 'Khấu hao', endpoint: '/fa/depreciation-runs', description: 'Các kỳ chạy khấu hao.', actions: [{ key: 'post', label: 'Ghi sổ', allowedStatuses: ['COMPLETED'] }] },
      { key: 'disposals', label: 'Thanh lý', endpoint: '/fa/disposals', description: 'Ghi giảm và lãi/lỗ thanh lý.', actions: [{ key: 'post', label: 'Ghi sổ', allowedStatuses: ['APPROVED'] }] },
      { key: 'stocktakes', label: 'Kiểm kê', endpoint: '/fa/stocktakes', description: 'Đợt và kết quả kiểm kê.' },
      { key: 'ccdc', label: 'Công cụ dụng cụ', endpoint: '/fa/ccdc-items', description: 'CCDC phân bổ nhiều kỳ.' },
    ],
  },
  {
    code: 'bg',
    label: 'Ngân sách',
    description: 'Chu kỳ, phiên bản, phê duyệt, cam kết và điều chỉnh ngân sách.',
    resources: [
      { key: 'cycles', label: 'Chu kỳ ngân sách', endpoint: '/bg/budget-cycles', description: 'Năm và trạng thái chu kỳ.' },
      { key: 'versions', label: 'Phiên bản ngân sách', endpoint: '/bg/budget-versions', description: 'Lập và phê duyệt baseline.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
      { key: 'items', label: 'Khoản mục', endpoint: '/bg/budget-items', description: 'Khoản mục và chế độ kiểm soát.', actions: ACTIVATION_ACTIONS },
      { key: 'adjustments', label: 'Điều chỉnh', endpoint: '/bg/budget-adjustments', description: 'Điều chuyển hoặc bổ sung ngân sách.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
    ],
  },
  {
    code: 'pr',
    label: 'Mua sắm',
    description: 'Yêu cầu mua, báo giá, đơn hàng, nhận hàng, hợp đồng và NCC.',
    resources: [
      { key: 'requisitions', label: 'Yêu cầu mua', endpoint: '/pr/requisitions', description: 'Yêu cầu mua và kiểm tra ngân sách.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
      { key: 'quotations', label: 'Báo giá', endpoint: '/pr/quotations', description: 'So sánh và chọn nhà cung cấp.' },
      { key: 'orders', label: 'Đơn mua hàng', endpoint: '/pr/purchase-orders', description: 'PO và cam kết ngân sách.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
      { key: 'receipts', label: 'Nhận hàng', endpoint: '/pr/goods-receipts', description: 'Phiếu nhận hàng/nghiệm thu.' },
      { key: 'contracts', label: 'Hợp đồng', endpoint: '/pr/contracts', description: 'Hợp đồng khung và cảnh báo hiệu lực.' },
      { key: 'ratings', label: 'Đánh giá NCC', endpoint: '/pr/vendor-ratings', description: 'Điểm chất lượng, giá và giao hàng.' },
    ],
  },
  {
    code: 'hr',
    label: 'Nhân sự & tiền lương',
    description: 'Hồ sơ, chấm công, nghỉ phép, bảng lương và phiếu lương.',
    resources: [
      { key: 'employees', label: 'Nhân viên', endpoint: '/hr/employees', description: 'Hồ sơ và quá trình công tác.' },
      { key: 'attendance', label: 'Chấm công', endpoint: '/hr/attendance', description: 'Bảng công theo kỳ.' },
      { key: 'leave', label: 'Nghỉ phép', endpoint: '/hr/leave-requests', description: 'Đơn nghỉ và số dư phép.', actions: DOCUMENT_ACTIONS.slice(0, 2) },
      { key: 'payroll', label: 'Bảng lương', endpoint: '/hr/payroll-runs', description: 'Tính, duyệt, chốt và hạch toán lương.', actions: DOCUMENT_ACTIONS },
    ],
  },
  {
    code: 'rp',
    label: 'Báo cáo & phân tích',
    description: 'Định nghĩa báo cáo, chạy nền, hợp nhất và xuất Excel/PDF.',
    resources: [
      { key: 'definitions', label: 'Định nghĩa báo cáo', endpoint: '/rp/report-definitions', description: 'Công thức báo cáo tài chính cấu hình được.', actions: ACTIVATION_ACTIONS },
    ],
  },
];

export function findModule(code: string | undefined): ErpModule | undefined {
  return ERP_MODULES.find((module) => module.code === code?.toLowerCase());
}
